/**
 * Chat routing for the demo API.
 *
 * In the published (read-only) build the composer is disabled and the
 * transcript is seeded directly into the thread store, so this path is not
 * reachable from the UI. It stays wired for the dev demo and for any
 * non-browser client, and it answers from the SAME recorded turns rather than
 * a second, drifting copy.
 */

import { RECORDED_TURNS, type RecordedTurn } from './transcript';

export interface DemoThinkingStep {
  event: string;
  data: Record<string, unknown>;
  /** ms after the previous event. */
  delay: number;
}

export interface DemoResponse {
  id: string;
  intent_type: string;
  tools_used: string[];
  /** Section 1 — streamed token-by-token, then pinned as authoritative_final_text. */
  s1: string;
  structured?: Record<string, unknown>;
  rag_sources?: Array<Record<string, unknown>>;
  extraMetadata?: Record<string, unknown>;
  suggestions: string[];
  processing_time_ms: number;
  thinking: DemoThinkingStep[];
}

/** A thinking script sized to the step count the captured run displayed. */
function thinkingFor(turn: RecordedTurn): DemoThinkingStep[] {
  const steps: DemoThinkingStep[] = [
    { event: 'session.started', data: { message: 'Session started' }, delay: 80 },
    { event: 'guardrail.input.checked', data: { passed: true, duration_ms: 120 }, delay: 200 },
    {
      event: 'planner.status_update',
      data: { message: 'Analyzing the question and selecting tools', status: 'done', step_name: 'route_selection' },
      delay: 260,
    },
  ];
  for (const tool of turn.tools_used) {
    steps.push({ event: 'orchestrator.tool_called', data: { tool_name: tool }, delay: 240 });
    steps.push({
      event: 'orchestrator.tool_result',
      data: { tool_name: tool, status: 'success', result_summary: 'ok', processing_time_ms: 380 },
      delay: 400,
    });
  }
  steps.push(
    { event: 'guardrail.output.checked', data: { passed: true, duration_ms: 90 }, delay: 220 },
    { event: 'thinking.complete', data: { status: 'done' }, delay: 150 },
  );
  return steps;
}

function toResponse(turn: RecordedTurn): DemoResponse {
  return {
    id: turn.id,
    intent_type: turn.intent_type,
    tools_used: turn.tools_used,
    s1: turn.answer,
    structured: turn.structured,
    suggestions: turn.suggestions,
    processing_time_ms: turn.processing_time_ms,
    thinking: thinkingFor(turn),
  };
}

/** Matched loosely, so the dev demo still answers a re-typed question. */
const MATCHERS: Record<string, RegExp> = {
  bp: /\bbp\b|blood pressure|diastolic|systolic/i,
  conditions: /^\/conditions|problem list|active conditions/i,
  'aortic-caregap': /care gap/i,
  'renal-creatinine': /renal function|creatinine/i,
};

const ROUTES: Array<{ match: RegExp; response: DemoResponse }> = RECORDED_TURNS.map((turn) => ({
  match: MATCHERS[turn.id] ?? new RegExp(turn.question.slice(0, 24).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'),
  response: toResponse(turn),
}));

/** The honest terminal for anything not in the recording (rule 16 edge case:
 *  Section 1 carries the message, Section 2 is omitted, chips still ship). */
const clarification: DemoResponse = {
  id: 'clarification',
  intent_type: 'clarification',
  tools_used: [],
  s1:
    'This is a recorded demo. The questions in this session were asked against the live system and their ' +
    'answers captured; anything outside that recording has no answer to show.',
  suggestions: RECORDED_TURNS.map((t) => t.question),
  processing_time_ms: 950,
  thinking: [
    { event: 'session.started', data: { message: 'Session started' }, delay: 80 },
    { event: 'guardrail.input.checked', data: { passed: true, duration_ms: 110 }, delay: 200 },
    { event: 'thinking.complete', data: { status: 'done' }, delay: 150 },
  ],
};

export function pickDemoResponse(message: string): DemoResponse {
  const text = (message || '').trim();
  for (const route of ROUTES) {
    if (route.match.test(text)) return route.response;
  }
  return clarification;
}
